import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import type {
  FieldSection,
  LayoutBlock,
  DetailFormApi,
  DetailFormBeforeFieldRenderer,
  RichTextOptions,
  DetailFormCustomFieldRenderer,
} from './types';
import type { ColumnMetadata, LookupDataItem } from '../grid';
import type { RelationPickerTarget } from '../grid/components/dialogs/RelationPickerDialog';
import {
  getFieldOccurrences,
  resolveFieldOccurrenceMetadata,
  type FieldOccurrenceResolver,
} from './resolveFieldOccurrences';
import { DetailFormSection } from './DetailFormSection';
import { DetailFormGridSection } from './DetailFormGridSection';
import { DetailFormKanbanSection } from './DetailFormKanbanSection';
import { DetailFormSpecificationSection } from './DetailFormSpecificationSection';
import { StyledTabs, Tab } from '../grid/components/common';
import {
  DETAIL_SECTION_ROW_SX,
  getResponsiveGridColumnSx,
  normalizeResponsiveWidth,
} from './responsiveWidthUtils';
import { usePersistedTabIndex } from './hooks/usePersistedTabIndex';

type AvailableChild = {
  id: number;
  key: string;
  name: string;
  contentType: string;
};

type DetailFormBlockProps = {
  block: LayoutBlock;
  fields: ColumnMetadata[];
  form: DetailFormApi;
  lookupMaps?: Record<string, Record<string, string>>;
  lookupOptionMaps?: Record<string, Record<string, string>>;
  lookupDataArrays?: Record<string, LookupDataItem[]>;
  disabled?: boolean;
  mode: 'add' | 'edit';
  parentContentKey?: string;
  parentId?: string;
  detailFormKey?: string;
  parentGridKey?: string;
  menuItemId?: number;
  availableChildren?: AvailableChild[];
  pruneEmptyTabs?: boolean;
  richTextOptions?: RichTextOptions;
  dataSource?: string;
  layoutRowChild?: boolean;
  onRegisterRelationTarget?: (target: RelationPickerTarget) => () => void;
  resolveFieldOccurrence?: FieldOccurrenceResolver;
  customFieldRenderer?: DetailFormCustomFieldRenderer;
  renderBeforeField?: DetailFormBeforeFieldRenderer;
  initialDateRange?: { startDate?: string; endDate?: string };
  tabsKey?: string;
};

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
  keepMounted?: boolean;
};

const TabPanel = ({ children, value, index, keepMounted = false }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      style={
        value === index
          ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
          : undefined
      }
    >
      {(value === index || keepMounted) && (
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const normalizeSection = (raw: FieldSection): FieldSection => ({
  ...raw,
  fieldOccurrences: raw.fieldOccurrences,
  fieldNames: raw.fieldNames ?? [],
  fieldKeys: raw.fieldKeys ?? [],
  width: normalizeResponsiveWidth(raw.width),
  captionsWidth: normalizeResponsiveWidth(raw.captionsWidth),
});

const isFieldVisible = (fieldMetadata: ColumnMetadata) => {
  if (fieldMetadata.canView === false) return false;
  return true;
};

const getFieldSections = (block: LayoutBlock): FieldSection[] => block.fieldSections ?? [];

/**
 * Resolves the ColumnMetadata for a configured field occurrence within a section,
 * preferring the position-based resolver (which correctly disambiguates
 * duplicate dataFields, e.g. multiple zReactNode widgets sharing a placeholder
 * dataField) and falling back to a plain dataField lookup when no resolver is
 * available or no entry was found for that position.
 */
const resolveFieldMetadataAt = (
  section: FieldSection,
  indexInSection: number,
  fields: ColumnMetadata[],
  resolveFieldOccurrence?: FieldOccurrenceResolver
): ColumnMetadata | undefined => {
  const occurrence = getFieldOccurrences(section)[indexInSection];
  if (!occurrence) return undefined;

  return (
    resolveFieldOccurrence?.(section.key, indexInSection) ??
    resolveFieldOccurrenceMetadata(occurrence, fields)
  );
};

const hasVisibleFieldsInSection = (
  section: FieldSection,
  fields: ColumnMetadata[],
  resolveFieldOccurrence?: FieldOccurrenceResolver
) => {
  return getFieldOccurrences(section).some((_, indexInSection) => {
    const fieldMetadata = resolveFieldMetadataAt(
      section,
      indexInSection,
      fields,
      resolveFieldOccurrence
    );
    return !!fieldMetadata && isFieldVisible(fieldMetadata);
  });
};

const getBlockContentType = (block: LayoutBlock) => block.contentType ?? '';

/**
 * Returns true if the block (or any of its descendants) renders a stateful
 * interactive section — currently grids and specifications — that must stay
 * mounted even when its parent tab is not active, to avoid losing local state
 * (filters, pagination, pending edits) on tab switches.
 */
const containsStatefulSection = (block: LayoutBlock): boolean =>
  block.contentType === 'zDashboardBlockGridSection' ||
  block.contentType === 'zDashboardBlockSpecification' ||
  (block.children ?? []).some(containsStatefulSection);

const hasRenderableContent = (
  block: LayoutBlock,
  fields: ColumnMetadata[],
  mode: 'add' | 'edit',
  menuItemId?: number,
  resolveFieldOccurrence?: FieldOccurrenceResolver
): boolean => {
  switch (getBlockContentType(block)) {
    case 'zKanban':
      return true;
    case 'zDashboardBlockGridSection':
      return !!menuItemId;
    case 'zDashboardBlockSpecification':
      return true;
    default:
      break;
  }

  const sections = getFieldSections(block);
  if (
    sections.some((section) => hasVisibleFieldsInSection(section, fields, resolveFieldOccurrence))
  ) {
    return true;
  }

  return (block.children || []).some((child) =>
    hasRenderableContent(child, fields, mode, menuItemId, resolveFieldOccurrence)
  );
};

const getBlockWidthSx = (block: LayoutBlock, layoutRowChild?: boolean) =>
  layoutRowChild ? getResponsiveGridColumnSx(block.width) : { width: '100%' as const };

const renderBlockBody = ({
  block,
  fields,
  form,
  lookupMaps,
  lookupOptionMaps,
  lookupDataArrays,
  disabled,
  mode,
  parentContentKey,
  parentId,
  detailFormKey,
  parentGridKey,
  menuItemId,
  availableChildren,
  pruneEmptyTabs,
  richTextOptions,
  dataSource,
  onRegisterRelationTarget,
  resolveFieldOccurrence,
  customFieldRenderer,
  renderBeforeField,
  initialDateRange,
  tabsKey,
}: BlockContentProps) => {
  switch (getBlockContentType(block)) {
    case 'zKanban':
      return <DetailFormKanbanSection block={block} mode={mode} />;
    case 'zDashboardBlockGridSection':
      return (
        <DetailFormGridSection
          block={block}
          parentId={parentId || ''}
          detailFormKey={detailFormKey}
          parentGridKey={parentGridKey}
          tabsKey={tabsKey}
          mode={mode}
          availableChildren={availableChildren}
          onRegisterRelationTarget={onRegisterRelationTarget}
          initialDateRange={initialDateRange}
        />
      );
    case 'zDashboardBlockSpecification':
      return (
        <DetailFormSpecificationSection
          block={block}
          parentId={parentId || ''}
          mode={mode}
          onRegisterRelationTarget={onRegisterRelationTarget}
          contentKey={parentContentKey}
        />
      );
    default:
      break;
  }

  const sections = getFieldSections(block);
  const hasSectionRow = sections.length > 0 || (block.children?.length ?? 0) > 0;

  const sectionRow = (
    <>
      {sections.map((section) => (
        <DetailFormSection
          key={section.key}
          section={normalizeSection(section)}
          fields={fields}
          form={form}
          lookupMaps={lookupMaps}
          lookupOptionMaps={lookupOptionMaps}
          lookupDataArrays={lookupDataArrays}
          disabled={disabled}
          mode={mode}
          richTextOptions={richTextOptions}
          contentKey={parentContentKey}
          parentId={parentId}
          dataSource={dataSource}
          resolveFieldOccurrence={resolveFieldOccurrence}
          customFieldRenderer={customFieldRenderer}
          renderBeforeField={renderBeforeField}
        />
      ))}

      {block.children?.map((nestedBlock) => (
        <DetailFormBlock
          key={nestedBlock.key}
          block={nestedBlock}
          fields={fields}
          form={form}
          lookupMaps={lookupMaps}
          lookupOptionMaps={lookupOptionMaps}
          lookupDataArrays={lookupDataArrays}
          disabled={disabled}
          mode={mode}
          parentContentKey={parentContentKey}
          parentId={parentId}
          detailFormKey={detailFormKey}
          parentGridKey={parentGridKey}
          menuItemId={menuItemId}
          availableChildren={availableChildren}
          pruneEmptyTabs={pruneEmptyTabs}
          richTextOptions={richTextOptions}
          dataSource={dataSource}
          onRegisterRelationTarget={onRegisterRelationTarget}
          resolveFieldOccurrence={resolveFieldOccurrence}
          customFieldRenderer={customFieldRenderer}
          renderBeforeField={renderBeforeField}
          initialDateRange={initialDateRange}
          layoutRowChild
        />
      ))}
    </>
  );

  return hasSectionRow ? <Box sx={DETAIL_SECTION_ROW_SX}>{sectionRow}</Box> : null;
};

type BlockContentProps = {
  block: LayoutBlock;
  fields: ColumnMetadata[];
  form: DetailFormApi;
  lookupMaps?: Record<string, Record<string, string>>;
  lookupOptionMaps?: Record<string, Record<string, string>>;
  lookupDataArrays?: Record<string, LookupDataItem[]>;
  disabled?: boolean;
  mode: 'add' | 'edit';
  parentContentKey?: string;
  parentId?: string;
  detailFormKey?: string;
  parentGridKey?: string;
  menuItemId?: number;
  availableChildren?: AvailableChild[];
  pruneEmptyTabs?: boolean;
  richTextOptions?: RichTextOptions;
  dataSource?: string;
  layoutRowChild?: boolean;
  onRegisterRelationTarget?: (target: RelationPickerTarget) => () => void;
  resolveFieldOccurrence?: FieldOccurrenceResolver;
  customFieldRenderer?: DetailFormCustomFieldRenderer;
  renderBeforeField?: DetailFormBeforeFieldRenderer;
  initialDateRange?: { startDate?: string; endDate?: string };
  tabsKey?: string;
};

export const DetailFormBlock = ({
  block,
  fields,
  form,
  lookupMaps,
  lookupOptionMaps,
  lookupDataArrays,
  disabled = false,
  mode,
  parentContentKey,
  parentId,
  detailFormKey,
  parentGridKey,
  menuItemId,
  availableChildren,
  pruneEmptyTabs = false,
  richTextOptions,
  dataSource,
  layoutRowChild = false,
  onRegisterRelationTarget,
  resolveFieldOccurrence,
  customFieldRenderer,
  renderBeforeField,
  initialDateRange,
  tabsKey,
}: DetailFormBlockProps) => {
  const { activeTab: storedTab, handleTabChange } = usePersistedTabIndex(block.key);

  const blockStyle: React.CSSProperties = {
    minHeight: block.minHeight > 0 ? `${block.minHeight}px` : undefined,
    backgroundColor: block.backgroundColor || undefined,
  };

  if (
    pruneEmptyTabs &&
    !hasRenderableContent(block, fields, mode, menuItemId, resolveFieldOccurrence)
  ) {
    return null;
  }

  const blockContentProps: BlockContentProps = {
    block,
    fields,
    form,
    lookupMaps,
    lookupOptionMaps,
    lookupDataArrays,
    disabled,
    mode,
    parentContentKey,
    parentId,
    detailFormKey,
    parentGridKey,
    menuItemId,
    availableChildren,
    pruneEmptyTabs,
    richTextOptions,
    dataSource,
    layoutRowChild,
    onRegisterRelationTarget,
    resolveFieldOccurrence,
    customFieldRenderer,
    renderBeforeField,
    initialDateRange,
    tabsKey,
  };

  const getBlockContentProps = (blockToRender: LayoutBlock): BlockContentProps => ({
    ...blockContentProps,
    block: blockToRender,
  });

  const childBlocks = pruneEmptyTabs
    ? (block.children || []).filter((child) =>
        hasRenderableContent(child, fields, mode, menuItemId, resolveFieldOccurrence)
      )
    : block.children || [];

  // Clamp stored tab index to valid range — guards against stale sessionStorage value
  // from a previous form that had more tabs than the current one.
  const activeTab = childBlocks.length > 0 ? Math.min(storedTab, childBlocks.length - 1) : 0;

  if (block.viewAs === 'tab-view' && childBlocks.length > 0) {
    return (
      <Box
        sx={{
          mb: 1.5,
          ...blockStyle,
          ...getBlockWidthSx(block, layoutRowChild),
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: block.minHeight > 0 ? `${block.minHeight}px` : 0,
          overflow: 'hidden',
          backgroundColor: 'background.default',
        }}
      >
        {block.title && (
          <Box sx={{ px: 1.5, pt: 1.5, flexShrink: 0 }}>
            <Typography variant="h5">{block.title}</Typography>
            {block.subTitle && (
              <Typography variant="body2" color="text.secondary">
                {block.subTitle}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ flexShrink: 0 }}>
          <StyledTabs value={activeTab} onChange={handleTabChange}>
            {childBlocks.map((child, index) => (
              <Tab key={child.key} label={child.title || 'Main'} id={`tab-${index}`} />
            ))}
          </StyledTabs>
        </Box>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: 1,
            borderRadius: '4px',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1,
              '&::-webkit-scrollbar': { width: 10 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: 8,
                border: '2px solid transparent',
                backgroundClip: 'padding-box',
              },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            }}
          >
            {childBlocks.map((child, index) => {
              return (
                <TabPanel
                  key={child.key}
                  value={activeTab}
                  index={index}
                  keepMounted={containsStatefulSection(child)}
                >
                  {renderBlockBody({ ...getBlockContentProps(child), tabsKey: block.key })}
                </TabPanel>
              );
            })}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{ p: 1.5, mb: 1.5, ...blockStyle, ...getBlockWidthSx(block, layoutRowChild) }}
    >
      {block.title && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h5">{block.title}</Typography>
          {block.subTitle && (
            <Typography variant="body2" color="text.secondary">
              {block.subTitle}
            </Typography>
          )}
        </Box>
      )}

      {renderBlockBody(blockContentProps)}
    </Paper>
  );
};
